import React, { useState } from 'react';
import mbIcon from '../assets/mb_icon.png';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getApp } from 'firebase/app';

interface BecomeAPartnerProps {
  onBack: () => void;
}

const app = getApp();
const db = getFirestore(app);

const BecomeAPartner: React.FC<BecomeAPartnerProps> = ({ onBack }) => {
  const [formData, setFormData] = useState({
    contactName: '',
    email: '',
    contactNumber: '',
    organizationName: '',
    partnerType: '',
    city: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.contactName || !formData.email || !formData.contactNumber) {
      alert('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      // Save to Firestore - this will trigger Cloud Function to send email
      await addDoc(collection(db, 'partner_requests'), {
        contactName: formData.contactName,
        email: formData.email,
        contactNumber: formData.contactNumber,
        organizationName: formData.organizationName || 'Not provided',
        partnerType: formData.partnerType || 'Not specified',
        city: formData.city || 'Not provided',
        message: formData.message || 'No message provided',
        timestamp: serverTimestamp(),
        status: 'new'
      });

      alert('Thank you for your interest in partnering with us! We will get back to you soon.');

      setFormData({
        contactName: '',
        email: '',
        contactNumber: '',
        organizationName: '',
        partnerType: '',
        city: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Sorry, there was an error sending your request. Please try again or email us directly at contact@mylearningboard.com');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <div className="bg-primary-custom text-white py-5">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center">
              <img src={mbIcon} alt="My Learning Board" style={{ width: '100px', height: '100px', marginRight: '20px' }} />
              <div>
                <h1 className="mb-2">Become A Partner</h1>
                <p className="mb-0">Join hands with My Learning Board and grow together!</p>
                <p className="mb-0">We are looking for passionate partners to expand quality education technology.</p>
              </div>
            </div>
            <button className="btn btn-light" onClick={onBack}>
              <i className="bi bi-arrow-left me-2"></i>
              Back
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-5">
        <div className="row g-4">
          {/* Left Section - Why Partner */}
          <div className="col-lg-5">
            <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: '#e8f0f7' }}>
              <div className="card-body py-4 px-4">
                <h5 className="mb-3 fw-bold text-dark">Why Partner With Us?</h5>
                <ul className="list-unstyled mb-0">
                  <li className="d-flex align-items-start mb-3">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span>Access to a cutting-edge AI-powered school management platform</span>
                  </li>
                  <li className="d-flex align-items-start mb-3">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span>Attractive revenue sharing and commission models</span>
                  </li>
                  <li className="d-flex align-items-start mb-3">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span>Dedicated partner support and training</span>
                  </li>
                  <li className="d-flex align-items-start mb-3">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span>Marketing and sales collateral provided</span>
                  </li>
                  <li className="d-flex align-items-start mb-3">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span>Growing EdTech market with high demand</span>
                  </li>
                  <li className="d-flex align-items-start">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span>Be part of transforming education with Agentic AI</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Partner Types */}
            <div className="card border-0 shadow-sm">
              <div className="card-body py-4 px-4">
                <h5 className="mb-3 fw-bold text-dark">Partnership Types</h5>
                <div className="d-flex align-items-start mb-3">
                  <div className="bg-primary-custom text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: '40px', height: '40px' }}>
                    <i className="bi bi-building"></i>
                  </div>
                  <div>
                    <h6 className="mb-1">Channel Partner</h6>
                    <p className="text-muted small mb-0">Resell My Learning Board to schools in your region</p>
                  </div>
                </div>
                <div className="d-flex align-items-start mb-3">
                  <div className="bg-primary-custom text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: '40px', height: '40px' }}>
                    <i className="bi bi-people"></i>
                  </div>
                  <div>
                    <h6 className="mb-1">Referral Partner</h6>
                    <p className="text-muted small mb-0">Refer schools and earn commission on every conversion</p>
                  </div>
                </div>
                <div className="d-flex align-items-start mb-3">
                  <div className="bg-primary-custom text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: '40px', height: '40px' }}>
                    <i className="bi bi-gear"></i>
                  </div>
                  <div>
                    <h6 className="mb-1">Implementation Partner</h6>
                    <p className="text-muted small mb-0">Help schools implement and onboard onto the platform</p>
                  </div>
                </div>
                <div className="d-flex align-items-start">
                  <div className="bg-primary-custom text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: '40px', height: '40px' }}>
                    <i className="bi bi-laptop"></i>
                  </div>
                  <div>
                    <h6 className="mb-1">Technology Partner</h6>
                    <p className="text-muted small mb-0">Integrate your solutions with My Learning Board</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section - Form */}
          <div className="col-lg-7">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-4">
                <h4 className="mb-4 text-dark fw-bold">Partner With Us</h4>
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      placeholder="Contact Name *"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <input
                      type="email"
                      className="form-control form-control-lg"
                      placeholder="Email Address *"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <input
                      type="tel"
                      className="form-control form-control-lg"
                      placeholder="Contact Number *"
                      name="contactNumber"
                      value={formData.contactNumber}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      placeholder="Organization Name"
                      name="organizationName"
                      value={formData.organizationName}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <select
                        className="form-select form-select-lg"
                        name="partnerType"
                        value={formData.partnerType}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Partnership Type</option>
                        <option value="channel_partner">Channel Partner</option>
                        <option value="referral_partner">Referral Partner</option>
                        <option value="implementation_partner">Implementation Partner</option>
                        <option value="technology_partner">Technology Partner</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        placeholder="City / Region"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <textarea
                      className="form-control"
                      rows={4}
                      placeholder="Tell us about your experience and why you'd like to partner (Optional)"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary-custom w-100 btn-lg"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Submitting...
                      </>
                    ) : (
                      'Submit Partnership Request'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information Cards */}
        <div className="row g-4 mt-4">
          <div className="col-md-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center py-5">
                <div className="mb-4">
                  <i className="bi bi-telephone-fill fs-1 text-primary-custom"></i>
                </div>
                <h4 className="mb-3">Phone</h4>
                <p className="mb-2">
                  <strong>Sales:</strong> <a href="tel:+918839393463" className="text-decoration-none">India:(+91) 8839393463</a>
                </p>
                <p className="mb-0">
                  <strong>Support:</strong> <a href="tel:+918839393463" className="text-decoration-none">India:(+91) 8839393463</a>
                </p>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center py-5">
                <div className="mb-4">
                  <i className="bi bi-headset fs-1 text-primary-custom"></i>
                </div>
                <h4 className="mb-3">Partnership Enquiries</h4>
                <p className="mb-0">
                  <a href="mailto:contact@mylearningboard.com" className="text-decoration-none">
                    contact@mylearningboard.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BecomeAPartner;
